import React, { useCallback, useRef } from 'react';
import './effectBlocks.css';

/**
 * PreviewCanvas — the shared editor surface for spatial effect blocks.
 *
 * Renders the source video frame and lays interactive blocks over it in
 * NORMALIZED coordinates (0..1 of frame width/height), so the preview at
 * display scale and the render at full resolution agree. Dragging updates a
 * block's x/y as fractions; the compiler resolves them to pixels using the real
 * media dimensions.
 *
 * Phase 1 renders the `text` block. New spatial block types add a branch in
 * renderBlock and reuse the same drag handling.
 *
 * Props:
 *   frameUrl    - poster/still or a video URL to show as the backdrop
 *   isVideo     - render frameUrl as a <video> (muted, seekable) vs an <img>
 *   renderFrame - supply the backdrop yourself instead of frameUrl. The editor
 *                 stage passes its compositor canvas here, so block placement,
 *                 dragging and resizing are the same code over a live timeline
 *                 as over a single clip in a modal.
 *   aspectRatio - width/height of the media, to size the surface
 *   blocks      - array of EffectBlock (spatial types)
 *   selectedId  - id of the currently selected block
 *   onChange    - (id, nextParams) => void, fired live during drag
 *   onSelect    - (id) => void
 *   onMeta      - ({width,height,duration}) => void, real media dims once known
 */
function PreviewCanvas({ frameUrl, isVideo = true, renderFrame, aspectRatio = 9 / 16, blocks = [], selectedId, onChange, onSelect, onMeta, stickerUrl, frameTransform, frameFilter }) {
    const surfaceRef = useRef(null);
    const dragRef = useRef(null);
    const frameStyle = (frameTransform || frameFilter)
        ? { transform: frameTransform, filter: frameFilter }
        : undefined;

    const beginDrag = useCallback((event, block) => {
        event.preventDefault();
        event.stopPropagation();
        onSelect?.(block.id);
        const surface = surfaceRef.current;
        if (!surface) return;
        const rect = surface.getBoundingClientRect();
        dragRef.current = {
            id: block.id,
            rect,
            // Offset between the pointer and the block anchor, in fractions,
            // so the block doesn't jump to the cursor on grab.
            offsetX: (block.params.x ?? 0.5) - (event.clientX - rect.left) / rect.width,
            offsetY: (block.params.y ?? 0.5) - (event.clientY - rect.top) / rect.height,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
    }, [onSelect]);

    const onDrag = useCallback((event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const { rect } = drag;
        const clamp = (v) => Math.max(0, Math.min(1, v));
        const block = blocks.find((b) => b.id === drag.id);
        if (!block) return;
        let x = clamp((event.clientX - rect.left) / rect.width + drag.offsetX);
        let y = clamp((event.clientY - rect.top) / rect.height + drag.offsetY);
        // A rectangle's x/y is its top-left corner, so keep it wholly in frame.
        if (block.type === 'box' || block.type === 'crop') {
            const w = block.params.w ?? 0.5;
            const h = block.params.h ?? 0.3;
            x = Math.max(0, Math.min(1 - w, x));
            y = Math.max(0, Math.min(1 - h, y));
        }
        onChange?.(drag.id, { ...block.params, x, y });
    }, [blocks, onChange]);

    const endDrag = useCallback((event) => {
        if (dragRef.current) {
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            dragRef.current = null;
        }
    }, []);

    // Corner-handle resize for rectangular blocks (box). The dragged corner moves
    // to the pointer; the opposite corner stays pinned, so x/y/w/h stay in frame.
    const resizeRef = useRef(null);

    const beginResize = useCallback((event, block, corner) => {
        event.preventDefault();
        event.stopPropagation();
        onSelect?.(block.id);
        const surface = surfaceRef.current;
        if (!surface) return;
        resizeRef.current = { id: block.id, corner, rect: surface.getBoundingClientRect() };
        event.currentTarget.setPointerCapture?.(event.pointerId);
    }, [onSelect]);

    const onResize = useCallback((event) => {
        const resize = resizeRef.current;
        if (!resize) return;
        const { rect, corner } = resize;
        const block = blocks.find((b) => b.id === resize.id);
        if (!block) return;
        const p = block.params || {};
        const px = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
        const py = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
        const MIN = 0.05;
        let x = p.x ?? 0.25;
        let y = p.y ?? 0.25;
        let w = p.w ?? 0.5;
        let h = p.h ?? 0.3;
        const right = x + w;
        const bottom = y + h;
        if (corner.includes('l')) { x = Math.min(px, right - MIN); w = right - x; }
        if (corner.includes('r')) { w = Math.max(MIN, px - x); }
        if (corner.includes('t')) { y = Math.min(py, bottom - MIN); h = bottom - y; }
        if (corner.includes('b')) { h = Math.max(MIN, py - y); }
        onChange?.(resize.id, { ...p, x, y, w, h });
    }, [blocks, onChange]);

    const endResize = useCallback((event) => {
        if (resizeRef.current) {
            event.currentTarget.releasePointerCapture?.(event.pointerId);
            resizeRef.current = null;
        }
    }, []);

    const renderBlock = (block) => {
        // A video inset. The caller paints the video itself onto its frame and
        // hands over the exact rect it painted, so this is only the handle that
        // moves it -- dragging updates the same centre x/y a sticker has.
        if (block.type === 'pip') {
            const { rect } = block;
            const selected = block.id === selectedId;
            return (
                <div
                    key={block.id}
                    className={`eb-pip-block${selected ? ' selected' : ''}`}
                    style={{
                        left: `${rect.left * 100}%`,
                        top: `${rect.top * 100}%`,
                        width: `${rect.width * 100}%`,
                        height: `${rect.height * 100}%`,
                    }}
                    onPointerDown={(e) => beginDrag(e, block)}
                    onPointerMove={onDrag}
                    onPointerUp={endDrag}
                    role="button"
                    tabIndex={0}
                    aria-label="Overlay video"
                />
            );
        }

        // `overlay` is a sticker whose image the editor generates from a
        // template rather than the user picking an upload — same placement.
        if (block.type === 'sticker' || block.type === 'overlay') {
            const p = block.params || {};
            const selected = block.id === selectedId;
            const stickerStyle = {
                left: `${(p.x ?? 0.5) * 100}%`,
                top: `${(p.y ?? 0.5) * 100}%`,
                width: `${(p.scale ?? 0.25) * 100}%`,
                transform: 'translate(-50%, -50%)',
            };
            return (
                <div
                    key={block.id}
                    className={`eb-sticker-block${selected ? ' selected' : ''}`}
                    style={stickerStyle}
                    onPointerDown={(e) => beginDrag(e, block)}
                    onPointerMove={onDrag}
                    onPointerUp={endDrag}
                    role="button"
                    tabIndex={0}
                    aria-label={block.type === 'overlay' ? 'Graphic overlay' : 'Sticker overlay'}
                >
                    {stickerUrl
                        ? <img className="eb-sticker-img" src={stickerUrl} alt="sticker" draggable={false} />
                        : <span className="eb-sticker-empty">Pick an image</span>}
                </div>
            );
        }
        if (block.type === 'zoom') {
            const p = block.params || {};
            const selected = block.id === selectedId;
            // The kept window is 1/zoom of the frame, centered on the focal point
            // and clamped in-frame — a preview of the punch-in framing.
            const zoom = Math.max(1.01, p.zoom || 1.5);
            const winW = 1 / zoom;
            const winH = 1 / zoom;
            const cx = Math.min(1 - winW / 2, Math.max(winW / 2, p.x ?? 0.5));
            const cy = Math.min(1 - winH / 2, Math.max(winH / 2, p.y ?? 0.5));
            const zoomStyle = {
                left: `${(cx - winW / 2) * 100}%`,
                top: `${(cy - winH / 2) * 100}%`,
                width: `${winW * 100}%`,
                height: `${winH * 100}%`,
            };
            return (
                <div
                    key={block.id}
                    className={`eb-zoom-window${selected ? ' selected' : ''}`}
                    style={zoomStyle}
                    onPointerDown={(e) => beginDrag(e, block)}
                    onPointerMove={onDrag}
                    onPointerUp={endDrag}
                    role="button"
                    tabIndex={0}
                    aria-label="Zoom focal point"
                >
                    <span className="eb-zoom-dot" aria-hidden="true" />
                    <span className="eb-zoom-label" aria-hidden="true">{`${Math.round(zoom * 100) / 100}×`}</span>
                </div>
            );
        }
        if (block.type === 'crop') {
            const p = block.params || {};
            const selected = block.id === selectedId;
            const cropStyle = {
                left: `${(p.x ?? 0.1) * 100}%`,
                top: `${(p.y ?? 0.1) * 100}%`,
                width: `${(p.w ?? 0.8) * 100}%`,
                height: `${(p.h ?? 0.8) * 100}%`,
            };
            return (
                <div
                    key={block.id}
                    className={`eb-crop-block${selected ? ' selected' : ''}`}
                    style={cropStyle}
                    onPointerDown={(e) => beginDrag(e, block)}
                    onPointerMove={onDrag}
                    onPointerUp={endDrag}
                    role="button"
                    tabIndex={0}
                    aria-label="Crop region"
                >
                    <span className="eb-crop-label" aria-hidden="true">Keep</span>
                    {selected && ['tl', 'tr', 'bl', 'br'].map((corner) => (
                        <i
                            key={corner}
                            className={`eb-grip ${corner} eb-grip-active`}
                            onPointerDown={(e) => beginResize(e, block, corner)}
                            onPointerMove={onResize}
                            onPointerUp={endResize}
                        />
                    ))}
                </div>
            );
        }
        if (block.type === 'box') {
            const p = block.params || {};
            const selected = block.id === selectedId;
            const boxStyle = {
                left: `${(p.x ?? 0.25) * 100}%`,
                top: `${(p.y ?? 0.25) * 100}%`,
                width: `${(p.w ?? 0.5) * 100}%`,
                height: `${(p.h ?? 0.3) * 100}%`,
                borderColor: p.color || '#d9363e',
                background: p.filled ? (p.color || '#d9363e') : 'transparent',
            };
            return (
                <div
                    key={block.id}
                    className={`eb-box-block${selected ? ' selected' : ''}`}
                    style={boxStyle}
                    onPointerDown={(e) => beginDrag(e, block)}
                    onPointerMove={onDrag}
                    onPointerUp={endDrag}
                    role="button"
                    tabIndex={0}
                    aria-label="Box overlay"
                >
                    {selected && ['tl', 'tr', 'bl', 'br'].map((corner) => (
                        <i
                            key={corner}
                            className={`eb-grip ${corner} eb-grip-active`}
                            onPointerDown={(e) => beginResize(e, block, corner)}
                            onPointerMove={onResize}
                            onPointerUp={endResize}
                        />
                    ))}
                </div>
            );
        }
        /* A veil over the whole frame, not a placeable block. It sits above the
           other blocks on purpose: ffmpeg's fade applies to the composited
           frame, so text burned into that frame fades with it -- a preview that
           kept the text bright through a fade to black would be lying. */
        if (block.type === 'fade') {
            return (
                <div
                    key={block.id}
                    className="eb-fade-veil"
                    style={{ opacity: Math.min(1, Math.max(0, block.params?.opacity ?? 0)) }}
                    aria-hidden="true"
                />
            );
        }

        if (block.type !== 'text') return null;
        const p = block.params || {};
        const selected = block.id === selectedId;
        // Font size in the preview scales with the surface height, matching how
        // fontFrac compiles (fontsize = fontFrac * mediaHeight).
        const fontStyle = {
            left: `${(p.x ?? 0.5) * 100}%`,
            top: `${(p.y ?? 0.5) * 100}%`,
            fontSize: `${(p.fontFrac ?? 0.05) * 100}cqh`,
            color: p.color || '#ffffff',
            fontFamily: FONT_CSS[p.fontFamily] || 'inherit',
            fontStyle: (p.fontFamily || '').includes('Italic') ? 'italic' : 'normal',
            textShadow: p.shadow ? '0 0.12em 0.35em rgba(0,0,0,0.6)' : 'none',
            textAlign: p.anchor === 'left' ? 'left' : p.anchor === 'right' ? 'right' : 'center',
            transform: `translate(${p.anchor === 'left' ? '0' : p.anchor === 'right' ? '-100%' : '-50%'}, -50%)`,
        };
        return (
            <div
                key={block.id}
                className={`eb-text-block${selected ? ' selected' : ''}`}
                style={fontStyle}
                onPointerDown={(e) => beginDrag(e, block)}
                onPointerMove={onDrag}
                onPointerUp={endDrag}
                role="button"
                tabIndex={0}
                aria-label={`Text: ${p.text || ''}`}
            >
                {p.box && <span className="eb-text-box" aria-hidden="true" />}
                <span className="eb-text-content">{p.text || 'Your text'}</span>
                {selected && (
                    <span className="eb-handles" aria-hidden="true">
                        <i className="eb-grip tl" /><i className="eb-grip tr" />
                        <i className="eb-grip bl" /><i className="eb-grip br" />
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="eb-stage">
            <div
                className="eb-surface"
                ref={surfaceRef}
                style={{ aspectRatio: String(aspectRatio) }}
                onPointerDown={() => onSelect?.(null)}
            >
                {/* The caller-supplied frame gets the same transform/filter the
                    built-in <video>/<img> would: a stage that renders its own
                    canvas should still show flips and colour grades. */}
                {renderFrame ? renderFrame(frameStyle) : frameUrl ? (
                    isVideo ? (
                        <video
                            className="eb-frame"
                            src={frameUrl}
                            muted
                            playsInline
                            preload="metadata"
                            style={frameStyle}
                            onLoadedMetadata={(e) => {
                                const v = e.currentTarget;
                                if (v.videoWidth && v.videoHeight) {
                                    onMeta?.({ width: v.videoWidth, height: v.videoHeight, duration: v.duration || 0 });
                                }
                            }}
                        />
                    ) : (
                        <img
                            className="eb-frame"
                            src={frameUrl}
                            alt="preview frame"
                            style={frameStyle}
                            onLoad={(e) => {
                                const img = e.currentTarget;
                                if (img.naturalWidth && img.naturalHeight) {
                                    onMeta?.({ width: img.naturalWidth, height: img.naturalHeight, duration: 0 });
                                }
                            }}
                        />
                    )
                ) : (
                    <div className="eb-frame eb-frame-empty">No preview available</div>
                )}
                {blocks.map(renderBlock)}
            </div>
        </div>
    );
}

// Preview-only font stand-ins; the render maps fontFamily via VIXO_FONT_FILES.
const FONT_CSS = {
    'Futura Bold': '"Futura", "Century Gothic", system-ui, sans-serif',
    'Segoe UI Semibold': '"Segoe UI Semibold", "Segoe UI", system-ui, sans-serif',
    'Impact': 'Impact, Haettenschweiler, sans-serif',
    'Georgia Italic': 'Georgia, serif',
};

export default PreviewCanvas;
